import moment from 'moment-timezone';
import { useState } from 'react';
import { createOrder } from '../../../../../../api/action/cart';
import { createBulkScheduledProducts, saveScheduledProduct } from '../../../../../../api/action/schedule';
import { isKioskSessionActive } from '../../../../../../domains/flexOffice/kiosk/kioskSession.js';

const useBookingFlow = (
  product,
  searchCriteria,
  selectedTime,
  companyState,
  companyPrimaryId,
  userAccount,
  isAuthenticated,
  guestAccountId,
  accountIdforPostMessage,
  dispatch,
  navigate
) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [scheduledProducts, setScheduledProducts] = useState([]);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const buildSingleScheduledProductPayload = (date, localUpdatedTimes, getActualEndTime) => {
    const timeData = localUpdatedTimes[date] || selectedTime;
    const actualEndTime = timeData?.end || getActualEndTime();

    return {
      period: {
        effectiveDate: {
          localStartDate: moment(date)
            .hour(timeData?.start?.split(':')[0] || 0)
            .minute(timeData?.start?.split(':')[1] || 0)
            .second(0)
            .format('YYYY-MM-DDTHH:mm:ss'),
          localEndDate: moment(date)
            .hour(actualEndTime?.split(':')[0] || 0)
            .minute(actualEndTime?.split(':')[1] || 0)
            .second(0)
            .format('YYYY-MM-DDTHH:mm:ss'),
          timeZone: companyState?.operatingLocation?.zoneId || 'America/New_York',
          considerTimePortion: true
        },
        durationType: "HOUR",
        duration: timeData?.duration || selectedTime?.duration,
      },
      owners: [],
      product: {
        primaryId: product.primaryId,
        type: product.type || null,
      },
      createdBy: userAccount?.name || "user",
      hasSchedule: true,
      status: "ACTIVE",
      company: { primaryId: companyPrimaryId },
      hasChild: false,
      parentProductId: null,
      associatedScheduledProducts: [],
      name: `${product.name} - ${moment(date).format('MMM DD, YYYY')}`,
      clients: [],
    };
  };

  const buildBulkScheduledProductsPayload = (dates, localUpdatedTimes, getActualEndTime) => {
    return dates.map(date => buildSingleScheduledProductPayload(date, localUpdatedTimes, getActualEndTime));
  };

  const checkEligibleDiscounts = async () => {
    return [];
  };

  const buildChildOrderPayload = (scheduledProduct, date, eligibleDiscounts = [], localUpdatedTimes, getQuantityForDate, pricePerHour) => {
    const quantity = getQuantityForDate(date);
    const totalAmount = parseFloat((pricePerHour * quantity).toFixed(2));

    let finalTotalAmount = totalAmount;

    if (eligibleDiscounts.length > 0) {
      const discount = eligibleDiscounts[0];
      if (discount.applyType === 'PERCENT' && discount.percentage) {
        const discountAmount = (totalAmount * discount.percentage) / 100;
        finalTotalAmount = parseFloat((totalAmount - discountAmount).toFixed(2));
      }
    }

    // Simplified child order payload - only includes required fields for grouped order
    return {
      createdBy: userAccount?.name || "User",
      lineItemList: [
        {
          quantity: quantity,
          totalAmount: finalTotalAmount,
          scheduledProduct: {
            primaryId: scheduledProduct.primaryId,
          },
          ...(eligibleDiscounts.length > 0 && { eligibleDiscounts })
        }
      ],
      company: {
        primaryId: companyPrimaryId,
        domain: {
          type: companyState?.domain?.type || "FLEX_OFFICE"
        }
      }
    };
  };

  const buildGroupedOrderPayload = (childOrderPayloads) => {
    // Calculate totals from all child orders
    const totalAmount = childOrderPayloads.reduce((sum, order) => {
      const orderTotal = order.lineItemList.reduce((itemSum, item) => itemSum + item.totalAmount, 0);
      return sum + orderTotal;
    }, 0);
    
    const totalLineItems = childOrderPayloads.reduce((sum, order) => sum + order.lineItemList.length, 0);
    const subtotalAmount = totalAmount;
    const totalBeforTax = totalAmount;

    return {
      createdBy: userAccount?.name || "User",
      accountId: userAccount?.account?.primaryId || guestAccountId || sessionStorage.getItem('guestAccountId') || null,
      totalAmount: totalAmount,
      totalLineItems: totalLineItems,
      subtotalAmount: subtotalAmount,
      tax: 0.0,
      shippingAndHandlingAmount: 0.0,
      totalBeforTax: totalBeforTax,
      shippingAddress: {
        street: companyState?.address?.street || "",
        city: companyState?.address?.city || "",
        state: companyState?.address?.state || "",
        zipCode: companyState?.address?.zipCode || "",
        country: companyState?.address?.country || ""
      },
      orderStatusHistoryList: [
        {
          status: "CREATED",
          eventDate: moment().toISOString()
        }
      ],
      company: {
        primaryId: companyPrimaryId,
        domain: {
          type: companyState?.domain?.type || "FLEX_OFFICE"
        }
      },
      currentStatus: "CREATED",
      isGroupedOrder: true, // Mark as grouped order
      childOrders: childOrderPayloads, // Include simplified child orders
      deliveryType: "BRICK_MORTAR"
    };
  };

  const buildBulkChildOrderPayloads = async (scheduledProductsList, dates, localUpdatedTimes, getQuantityForDate, pricePerHour, setCheckingDiscounts) => {
    const childOrderPayloads = [];
    setCheckingDiscounts(true);

    for (let i = 0; i < scheduledProductsList.length; i++) {
      const scheduledProduct = scheduledProductsList[i];
      const date = dates[i];

      const eligibleDiscounts = await checkEligibleDiscounts(scheduledProduct, date);

      const childOrderPayload = buildChildOrderPayload(scheduledProduct, date, eligibleDiscounts, localUpdatedTimes, getQuantityForDate, pricePerHour);
      childOrderPayloads.push(childOrderPayload);

      setProcessingProgress({ current: i + 1, total: scheduledProductsList.length });
    }

    setCheckingDiscounts(false);
    return childOrderPayloads;
  };

  const navigateToCheckinCheckout = (orderPrimaryId, companyState, accountIdforPostMessage) => {
    let guestPrimaryId = null;
    let accountId = sessionStorage.getItem('guestAccountId') || accountIdforPostMessage;
    let emailToUse = null;
    let passwordToUse = null;

    emailToUse = sessionStorage.getItem("encryptedEmail") || sessionStorage.getItem("flexoffice_email");
    passwordToUse = sessionStorage.getItem("encryptedPassword") || sessionStorage.getItem("flexoffice_password");

    const guestData = sessionStorage.getItem("guestData");
    if (guestData) {
      try {
        const parsedGuestData = JSON.parse(guestData);
        guestPrimaryId = parsedGuestData?.email || parsedGuestData?.primaryId || parsedGuestData?.id;
        accountId = accountId || parsedGuestData?.account?.primaryId || parsedGuestData?.accountId;
        emailToUse = emailToUse || parsedGuestData?.email;
      } catch {
        guestPrimaryId = null;
      }
    }

    guestPrimaryId = guestPrimaryId || emailToUse;
    const hasGuestCredentials = !!(emailToUse && passwordToUse);
    const isGuestUser = hasGuestCredentials || guestData || localStorage.getItem('guestAuthenticated') === 'true';

    if (isGuestUser) {
      const guestAuthData = {
        orderId: orderPrimaryId,
        accountId: accountId,
        email: emailToUse,
        password: passwordToUse,
        guestId: guestPrimaryId,
        timestamp: new Date().getTime()
      };

      sessionStorage.setItem('guestAuthData', JSON.stringify(guestAuthData));

      const params = new URLSearchParams();
      params.append('orderId', orderPrimaryId);

      if (accountId) {
        params.append('accountId', accountId);
      }

      if (emailToUse) {
        params.append('email', emailToUse);
      }

      if (guestPrimaryId) {
        params.append('guestId', guestPrimaryId);
      }

      const sessionToken = sessionStorage.getItem("encryptedPassword") || sessionStorage.getItem("flexoffice_password") || Date.now().toString(36);
      params.append('password', sessionToken);
      params.append('guestOrder', 'true');

      const companyCode = companyState?.primaryId || '';
      guestAuthData.sessionToken = sessionToken;
      sessionStorage.setItem('guestAuthData', JSON.stringify(guestAuthData));

      const isLocal = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('.local');

      let baseUrl;
      if (isLocal) {
        baseUrl = `${window.location.protocol}//${window.location.hostname}:4000`;
      } else {
        baseUrl = window.location.origin;
      }

      const redirectUrl = `${baseUrl}/console/reservation/guest-pay?${params.toString()}&companyCode=${companyCode}`;

      window.location.href = redirectUrl;

    } else {
      localStorage.removeItem('persistedOrder');
      sessionStorage.removeItem('pendingBooking');
      sessionStorage.removeItem('guestAuthData');

      navigate('/confirm-book', {
        state: {
          orderId: orderPrimaryId,
          bookingSuccess: true,
          message: `Your booking has been confirmed! Order ID: ${orderPrimaryId}`
        }
      });
    }
  };

  const handleSaveSchedule = async (
    occurrenceDates,
    recurrenceData,
    availabilityData,
    localUpdatedTimes,
    getQuantityForDate,
    pricePerHour,
    getActualEndTime,
    setCheckingDiscounts
  ) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    setIsProcessing(true);
    setError(null);
    setScheduledProducts([]);

    try {
      const datesToUse = occurrenceDates.length > 0 ? occurrenceDates : [searchCriteria?.date];
      const isSingleOccurrence = (!recurrenceData || datesToUse.length <= 1);

      if (isSingleOccurrence) {
        // Single booking - create one scheduled product and one order (not grouped)
        const singleDate = datesToUse[0];
        const scheduledProductPayload = buildSingleScheduledProductPayload(singleDate, localUpdatedTimes, getActualEndTime);
        const scheduledProductResponse = await saveScheduledProduct(scheduledProductPayload);

        const eligibleDiscounts = await checkEligibleDiscounts(scheduledProductResponse, singleDate);
        const orderPayload = buildChildOrderPayload(scheduledProductResponse, singleDate, eligibleDiscounts, localUpdatedTimes, getQuantityForDate, pricePerHour);
        
        // For single order, we need the full order payload (not just the simplified child version)
        const fullOrderPayload = {
          ...orderPayload,
          accountId: userAccount?.account?.primaryId || guestAccountId || sessionStorage.getItem('guestAccountId') || null,
          totalAmount: orderPayload.lineItemList[0].totalAmount,
          totalLineItems: 1,
          subtotalAmount: orderPayload.lineItemList[0].totalAmount,
          tax: 0.0,
          shippingAndHandlingAmount: 0.0,
          totalBeforTax: orderPayload.lineItemList[0].totalAmount,
          shippingAddress: {
            street: companyState?.address?.street || "",
            city: companyState?.address?.city || "",
            state: companyState?.address?.state || "",
            zipCode: companyState?.address?.zipCode || "",
            country: companyState?.address?.country || ""
          },
          orderStatusHistoryList: [
            {
              status: "CREATED",
              eventDate: moment().toISOString()
            }
          ],
          currentStatus: "CREATED",
          isChild: false,
          deliveryType: "BRICK_MORTAR"
        };
        
        const orderResponse = await createOrder(fullOrderPayload);

        if (orderResponse?.primaryId) {
          sessionStorage.setItem('orderPrimaryId', orderResponse.primaryId);
          navigateToCheckinCheckout(orderResponse.primaryId, companyState, accountIdforPostMessage);
          return;
        }

        localStorage.setItem('persistedOrder', JSON.stringify({
          order: orderResponse,
          scheduledProduct: scheduledProductResponse,
          timestamp: new Date().getTime()
        }));

        sessionStorage.removeItem('recurrenceData');
        sessionStorage.removeItem('pendingBooking');

        navigate('/confirm-book', { replace: true });
        return;
      } else {
        // Multiple bookings - create a grouped order with simplified child orders
        const availableDates = availabilityData
          ? datesToUse.filter(date => {
              const index = datesToUse.indexOf(date);
              return availabilityData[index]?.available;
            })
          : datesToUse;

        if (availableDates.length === 0) {
          throw new Error('No available dates found for the selected recurrence pattern');
        }

        if (availableDates.length < datesToUse.length) {
          const unavailableCount = datesToUse.length - availableDates.length;
          if (!window.confirm(`${unavailableCount} date(s) are not available. Continue booking ${availableDates.length} available date(s)?`)) {
            setIsProcessing(false);
            return;
          }
        }

        setProcessingProgress({ current: 0, total: availableDates.length });
        
        // Step 1: Create all scheduled products in bulk
        const bulkPayload = buildBulkScheduledProductsPayload(availableDates, localUpdatedTimes, getActualEndTime);
        const createdProducts = await createBulkScheduledProducts(bulkPayload);

        if (!createdProducts || !Array.isArray(createdProducts) || createdProducts.length === 0) {
          throw new Error('No scheduled products were created in bulk');
        }

        setScheduledProducts(createdProducts);
        
        // Step 2: Build simplified child order payloads for each scheduled product
        const childOrderPayloads = await buildBulkChildOrderPayloads(
          createdProducts, 
          availableDates, 
          localUpdatedTimes, 
          getQuantityForDate, 
          pricePerHour, 
          setCheckingDiscounts
        );
        
        // Step 3: Build and create a single grouped order containing all child orders
        const groupedOrderPayload = buildGroupedOrderPayload(childOrderPayloads);
        const orderResponse = await createOrder(groupedOrderPayload);

        if (orderResponse?.primaryId) {
          sessionStorage.setItem('orderPrimaryId', orderResponse.primaryId);
          navigateToCheckinCheckout(orderResponse.primaryId, companyState, accountIdforPostMessage);
          return;
        }

        localStorage.setItem('persistedOrder', JSON.stringify({
          order: orderResponse,
          scheduledProducts: createdProducts,
          timestamp: new Date().getTime()
        }));

        sessionStorage.removeItem('recurrenceData');
        sessionStorage.removeItem('pendingBooking');

        navigate('/confirm-book', {
          replace: true,
          state: {
            orderId: orderResponse?.primaryId,
            bookingSuccess: true,
            scheduledProductsCount: createdProducts.length,
            message: `Successfully scheduled ${createdProducts.length} sessions!`
          }
        });
      }

    } catch (error) {
      setError(error.message || 'An error occurred during processing');
    } finally {
      setIsProcessing(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  const handleLoginRequired = (bookingData) => {
    sessionStorage.setItem('pendingBooking', JSON.stringify(bookingData));
    setShowLoginModal(true);
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleNavigateToLogin = (navigate, bookingData) => {
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('loginRedirectUrl', currentPath);

    // On a kiosk device, route through the kiosk's own guest-only sign-in
    // screen instead of the public /sign-in page, so a staff/admin account
    // can never be signed in here — bookingData is already saved above as
    // sessionStorage['pendingBooking'], which KioskLogin resumes from on
    // success, the same way the public Login page already does.
    if (isKioskSessionActive()) {
      navigate('/kiosk/login', { state: { intent: 'book' } });
      return;
    }

    navigate('/sign-in', {
      state: {
        from: 'booking',
        bookingData
      }
    });
  };

  return {
    isProcessing,
    processingProgress,
    scheduledProducts,
    error,
    showLoginModal,
    handleSaveSchedule,
    handleLoginRequired,
    handleCloseLoginModal,
    handleNavigateToLogin,
    setError
  };
};

export default useBookingFlow;
